import BigNumber from 'bignumber.js';
import { computed, IReactionDisposer, observable, reaction } from 'mobx';
import { DepthStore } from './Depth';
import { DFT, TokenInfo } from '../canister/DFT';
import { parseToCommon, toPrecision } from '../utils/helper';
import { IDisposable } from '../interface';
import { TradeHistoryStore } from './TradeHistory';
import { KLineStore } from './KLine';
import { Fusion, SpotOrder } from '../canister/Fusion';
import { Market, MarketSetting } from '../canister/Market';
import { TickSpaceIndex } from '../canister/KLine';
import { AuthBase } from '../AuthBase';

export interface TickOf24Hours {
  lastPrice: BigNumber;
  high: BigNumber;
  low: BigNumber;
  open: BigNumber;
  close: BigNumber;
  volume: BigNumber;
  change: string;
}

export enum PriceTrend {
  Up = 'up',
  Down = 'down',
  Equal = 'equal'
}

export interface LatestPrice {
  price: number;
  trend: PriceTrend;
}

/**
 * Orderbook market
 */
export class MarketStore extends AuthBase implements IDisposable {
  @observable
  marketId: string;

  @observable
  marketSetting: MarketSetting;

  @observable
  quoteToken: TokenInfo;

  @observable
  baseToken: TokenInfo;

  @observable
  tickOf24H: TickOf24Hours;

  @observable
  depth: DepthStore;

  @observable
  tradeHistory: TradeHistoryStore;

  @observable
  kline: KLineStore;

  @observable
  myOpenOrders: Array<SpotOrder> = [];

  private baseTokenId: string;
  private quoteTokenId: string;
  private priceDecimals: number = 32;
  private priceDecimalsShow: number = 2;
  private initialized = false;
  private marketTickTimer?: NodeJS.Timer = undefined;
  private marketTickTimeInterval: number = 1000 * 10;
  private fetchMyOpenOrderTimer?: NodeJS.Timer = undefined;
  private fetchMyOpenOrderInterval: number = 1000 * 10;
  private sessionSupervisorDisposer?: IReactionDisposer;
  private fusionActor: Fusion;
  private marketActor = new Market();
  private subNewPrice = 0;

  /**
   * constructor, please call initialize() before use
   * @param baseTokenId  base token id
   * @param quotaTokenId  quote token id
   */
  constructor(baseTokenId: string, quotaTokenId: string) {
    super();
    this.baseTokenId = baseTokenId;
    this.quoteTokenId = quotaTokenId;
  }

  @computed
  public get latestPrice() {
    let trend = PriceTrend.Equal;
    const latestPrice = this.tickOf24H?.lastPrice?.toNumber() || 0;
    if (this.subNewPrice > latestPrice) {
      trend = PriceTrend.Down;
    } else if (this.subNewPrice < latestPrice) {
      trend = PriceTrend.Up;
    }
    return { price: latestPrice, trend };
  }

  /**
   * initialize
   */
  public initialize = async () => {
    if (this.initialized) throw new Error('MarketStore has been initialized');
    this.baseToken = await new DFT(this.baseTokenId).getTokenInfo();
    this.quoteToken = await new DFT(this.quoteTokenId).getTokenInfo();
    this.marketSetting = await this.marketActor.getMarketSetting(
      this.baseTokenId,
      this.quoteTokenId
    );
    this.marketId = this.marketSetting.marketId;
    this.depth = new DepthStore(this);
    this.kline = new KLineStore(this);
    this.tradeHistory = new TradeHistoryStore(this);
    this.fusionActor = new Fusion(
      this.marketId,
      this.marketSetting.priceDecimals
    );
    if (this.marketTickTimer === undefined) {
      this.startMarketTickTimer();
    }
    console.info(`MarketStore.initialized: ${this.marketId}`);
    this.initialized = true;

    this.sessionSupervisorDisposer = reaction(
      () => this.initialized && this.session.isConnected,
      async connected => {
        if (connected) {
          await this.updateMyOpenOrders();
          this.startFetchMyOpenOrdersTimer();
        } else {
          this.stopFetchMyOpenOrdersTimer();
          this.myOpenOrders = [];
        }
      },
      { fireImmediately: true }
    );
  };

  /**
   * fetch all my open orders
   */
  public updateMyOpenOrders = async () => {
    this.checkInitialized();
    if (!this.session.isConnected) return;
    const orders = await this.fusionActor.fetchOpenOrders();
    console.info(`MarketStore.fetchOpenOrders: ${orders.length}`);
    this.myOpenOrders = orders;
  };

  /**
   * submit ask limit order
   * @param price price of order
   * @param volume volume of order
   * @returns
   */
  public submitAskLimitOrder = async (
    price: bigint,
    volume: bigint
  ): Promise<bigint> => {
    this.checkInitialized();
    const res = await this.fusionActor.submitAskLimitOrder(price, volume);
    this.afterOrderOperation();
    return res;
  };

  /**
   * submit bid limit order
   * @param price price of order
   * @param volume volume of order
   * @returns
   */
  public submitBidLimitOrder = async (
    price: bigint,
    volume: bigint
  ): Promise<bigint> => {
    this.checkInitialized();
    const res = await this.fusionActor.submitBidLimitOrder(price, volume);
    this.afterOrderOperation();
    return res;
  };

  /**
   * cancel order
   * @param orderId open order id
   * @returns {Promise<Boolean>}
   */
  public cancelOrder = async (orderId: bigint) => {
    this.checkInitialized();
    const res = await this.fusionActor.cancelOrder(orderId);
    this.afterOrderOperation();
    return res;
  };

  /**
   * cancel all orders
   * @returns {Promise<Boolean>}
   */
  public cancelAllOrders = async () => {
    this.checkInitialized();
    const res = await this.fusionActor.cancelAllOrders();
    this.afterOrderOperation();
    return res;
  };
  /**
   * fetch market's last 24H tick
   * @returns {Promise<TickOf24Hours>}
   */
  private fetchTick = async () => {
    const tick = await this.kline.fetchCurrentTick(TickSpaceIndex.OneDay);
    if (!(tick && tick.close)) {
      return;
    }
    const lastPrice = parseToCommon(
      tick.close,
      this.priceDecimals,
      this.priceDecimalsShow
    );
    console.debug(`Market.fetchLastPrice: lastPrice: ${lastPrice}`);
    const low = parseToCommon(
      tick.low,
      this.priceDecimals,
      this.priceDecimalsShow
    );
    const high = parseToCommon(
      tick.high,
      this.priceDecimals,
      this.priceDecimalsShow
    );
    const open = parseToCommon(
      tick.open,
      this.priceDecimals,
      this.priceDecimalsShow
    );
    const close = parseToCommon(
      tick.close,
      this.priceDecimals,
      this.priceDecimalsShow
    );
    const volume = parseToCommon(
      tick.volume,
      this.baseToken?.decimals,
      this.priceDecimalsShow
    );
    const change = toPrecision(
      lastPrice.minus(open).div(open).multipliedBy(100),
      2
    ).toString();
    this.tickOf24H = { lastPrice, high, low, open, change, volume, close };
  };

  /**
   * order submitted/cancel hook
   */
  private afterOrderOperation = () => {
    this.stopFetchMyOpenOrdersTimer();
    this.startFetchMyOpenOrdersTimer();
    this.tradeHistory.updateUserTradeList();
  };

  private startMarketTickTimer = async () => {
    await this.fetchTick();
    this.fetchMyOpenOrderTimer = setInterval(() => {
      this.fetchTick();
    }, this.marketTickTimeInterval);
  };

  private stopMarketTickTimer = async () => {
    if (this.marketTickTimer) {
      clearInterval(this.marketTickTimer);
      this.marketTickTimer = undefined;
    }
  };

  private startFetchMyOpenOrdersTimer = () => {
    this.fetchMyOpenOrderTimer = setInterval(() => {
      if (!this.initialized) return;

      this.updateMyOpenOrders();
    }, this.fetchMyOpenOrderInterval);
  };

  private stopFetchMyOpenOrdersTimer = async () => {
    if (this.fetchMyOpenOrderTimer) {
      clearInterval(this.fetchMyOpenOrderTimer);
      this.fetchMyOpenOrderTimer = undefined;
    }
  };

  private checkInitialized = () => {
    if (!this.initialized)
      throw new Error('MarketStore has not been initialized');
  };

  /**
   * must be called before destroy, otherwise timer will not be cleared
   */
  dispose = (): void => {
    this.depth?.dispose();
    this.tradeHistory?.dispose();
    this.kline?.dispose();

    this.stopMarketTickTimer();
    this.stopFetchMyOpenOrdersTimer();

    if (this.sessionSupervisorDisposer) this.sessionSupervisorDisposer();

    this.initialized = false;
  };
}
